import { Type } from 'class-transformer';
import dayjs from 'dayjs';
import { Attachment } from './attachment';
import { BankTransactionBase } from './bank_transaction';
import { Model } from './model';
import { Partner } from './partner';
import { Payment } from './payment';
import { Email } from './email';
import { Call } from './call';
import { DeductionCreditMemo } from './deduction_credit_memo';
import { TransformStringToDayJS } from './utils';
import type { AgentMetrics, AgentParsingErrorCode } from './types';

export enum RemittanceStatus {
  SUBMITTED = 'submitted',

  PROCESSING = 'processing',

  INVALID = 'invalid',

  PROCESSED = 'processed',

  SPLIT = 'split',
}

export enum RemittanceSource {
  EMAIL = 'email',

  CALL = 'call',

  LOCKBOX_UPLOAD = 'lockbox_upload',

  PAYOUT = 'payout',

  MANUAL_UPLOAD = 'manual_upload',

  UNKNOWN = 'unknown',
}

export interface ChildRemittanceInfo {
  id: string;
}

export interface SplitRemittanceInfo {
  is_split_parent: boolean;
  split_count?: number;
  child_remittances: ChildRemittanceInfo[];
  parent_remittance_id?: string;
  split_index?: number;
}

export interface RemittanceBankDetails {
  account_number: string | null;

  bank_name: string | null;

  bank_number: string | null;

  branch_number: string | null;

  iban: string | null;
}

export class Remittance extends Model {
  id: string;

  agent_confidence_reason: string | null;

  agent_confidence_score: number | null;

  agent_error_code: AgentParsingErrorCode | null;

  agent_uncertainties: string[];

  @Type(() => RemittanceAllocation)
  allocations: RemittanceAllocation[];

  @Type(() => Attachment)
  attachment: Attachment | null;

  attachment_id: string | null;

  @Type(() => Attachment)
  attachments: Attachment[] = [];

  @Type(() => BankTransactionBase)
  bank_transaction: BankTransactionBase | null;

  bank_transaction_id: string | null;

  bank_transaction_ids: string[] = [];

  @Type(() => Call)
  call: Call | null;

  call_id: string | null;

  currency: string | null;

  @Type(() => Email)
  email: Email | null;

  email_id: string | null;

  payer_bank_details: RemittanceBankDetails | null;

  payer_email: string | null;

  payer_name: string | null;

  @Type(() => Payment)
  payments: Payment[];

  payment_method: string | null;

  status: RemittanceStatus;

  source: RemittanceSource;

  split_info: SplitRemittanceInfo | null;

  amount: number;

  transaction_amount: number;

  @TransformStringToDayJS(true)
  transaction_date: dayjs.Dayjs | null;

  transaction_number: string | null;

  get agentMetrics(): AgentMetrics | null {
    return this.agent_confidence_score !== null || this.agent_error_code
      ? {
          agentConfidenceReason: this.agent_confidence_reason,
          agentConfidenceScore: this.agent_confidence_score,
          agentErrorCode: this.agent_error_code,
          agentUncertainties: this.agent_uncertainties,
        }
      : null;
  }

  get agentConfidenceLevel(): 'high' | 'medium' | 'low' | null {
    return this.agent_confidence_score === null
      ? null
      : this.agent_confidence_score >= 95
        ? 'high'
        : this.agent_confidence_score >= 85
          ? 'medium'
          : 'low';
  }

  get isSplitParent(): boolean {
    return this.status === RemittanceStatus.SPLIT;
  }

  get isReady(): boolean {
    return (
      this.status !== RemittanceStatus.SUBMITTED && this.status !== RemittanceStatus.PROCESSING
    );
  }

  get isInvalid(): boolean {
    return this.status === RemittanceStatus.INVALID;
  }

  get partners(): Partner[] {
    const partners = this.allocations.map((a) => a.partner).filter((p): p is Partner => Boolean(p)); // now p is strictly defined Partner

    //  de-dupe allocation partners
    return Array.from(new Map(partners.map((p) => [p.id, p])).values());
  }

  get totalAllocated(): number {
    return this.allocations.reduce((sum, allocation) => sum + allocation.amount_paid, 0);
  }

  get totalUnallocated(): number {
    return this.amount - this.totalAllocated;
  }
}

export class RemittanceAllocation extends Model {
  amount_discount: number = 0;

  amount_paid: number = 0;

  amount_withheld: number = 0;

  deduction_code: string | null;

  deduction_quantity: string | null;

  deduction_reason: string | null;

  invoice_id: string | null;

  invoice_number: string | null;

  @Type(() => RemittanceAllocationInvoice)
  invoice: RemittanceAllocationInvoice | null = null;

  adjustment_id: string | null;

  credit_memo_id: string | null;

  credit_memo_number: string | null;

  @Type(() => DeductionCreditMemo)
  credit_memo: DeductionCreditMemo;

  // Partner support for multi-partner remittances
  partner_id: string | null;

  @Type(() => Partner)
  partner: Partner | null;

  get isInvalid(): boolean {
    if (this.invoice_id) {
      return (
        (this.invoice?.amount_remaining !== undefined &&
          this.amount_withheld + this.amount_paid > this.invoice.amount_due) ||
        this.amount_paid === 0
      );
    }
    if (this.credit_memo_id) {
      return this.amount_paid > this.credit_memo.net_amount || this.amount_paid === 0;
    }
    return false;
  }

  get isCredit(): boolean {
    return Boolean(this.credit_memo_id || this.credit_memo_number);
  }
}

export class RemittanceAllocationInvoice {
  id: string;

  amount_due: number;

  amount_paid: number;

  currency: string;

  @TransformStringToDayJS()
  due_date: dayjs.Dayjs | null;

  number: string | null;

  get amount_remaining(): number {
    return this.amount_due - this.amount_paid;
  }
}
